<script>
import date from 'locutus/php/datetime/date'
import defaults from 'lodash/defaults'
import {
  fetchPrivateMarket,
  fetchDirectMarkets,
  createPrivateMarket,
  updatePrivateMarket,
} from '@apis/admin/market'
import { fetchAssets } from '@apis/admin/asset'
import LangInput from '@components/admin/lang-input'
import FormEditor from '@components/admin/form-editor'
import { mapState } from 'vuex'

export default {
  components: { LangInput, FormEditor },
  props: {
    market_seq: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      params: {},
      assets: [],
      direct_markets: [],
      loading: false,
      active_tab: 'info',
    }
  },
  computed: {
    ...mapState('common', {
      constants: (state) => state.constants,
    }),
  },
  created() {
    this.init()
  },
  methods: {
    init() {
      this.loading = true
      Promise.all([
        this.loadMarket(),
        this.loadDirectMarkets(),
        this.loadAssets(),
      ])
        .catch((e) => this.$toasted.error(e.message))
        .finally(() => {
          this.loading = false
        })
    },
    async loadAssets() {
      const { data } = await fetchAssets()

      this.assets = data
    },
    async loadDirectMarkets() {
      const { data } = await fetchDirectMarkets()

      this.direct_markets = data
    },
    async loadMarket() {
      const { market_info } = await fetchPrivateMarket(this.market_seq)
      this.params = defaults(market_info, {
        title: '',
        use_follow_direct_market: 'N',
        follow_direct_market_seq: 0,
        use_min_spread_ratio: 'N',
        min_spread_ratio: 0,
        use_max_spread_ratio: 'N',
        max_spread_ratio: 0,
        fee_rate: 0,
        use_source_min_amount: 'N',
        source_min_amount: 0,
        use_source_max_amount: 'N',
        source_max_amount: 0,
        use_source_amount_unit: 'N',
        source_amount_unit: 0,
        is_active: 'Y',
        use_min_price: 'N',
        min_price: 0,
        use_max_price: 'N',
        max_price: 0,
        source_asset_seq: null,
        target_asset_seq: null,
        escrow_type: 'BOTH',
        seq: null,
        regdate: null,
      })
    },
    handleSubmit() {
      const createOrUpdate = this.params.seq
        ? updatePrivateMarket
        : createPrivateMarket
      const confirm_message = this.params.seq
        ? this.$t('admin.msg_save')
        : this.$t('admin.msg_enroll')
      const success_message = this.params.seq
        ? this.$t('admin.msg_c2c_modify')
        : this.$t('admin.msg_c2c_enroll')

      this.loading = true
      this.$confirm(confirm_message, this.$t('admin.modal_confirm'))
        .then(() => createOrUpdate(this.params))
        .then(({ seq }) => this.$emit('submit', seq))
        .then(() => this.$toasted.success(success_message))
        .catch((e) => (e ? this.$toasted.error(e.message) : null))
        .finally(() => {
          this.loading = false
        })
    },
    date,
  },
}
</script>

<template>
  <el-tabs v-model="active_tab" :stretch="true">
    <el-tab-pane :label="$t('admin.default_info')" name="info">
      <form
        v-loading="loading"
        action="./"
        method="get"
        @submit.prevent="handleSubmit"
      >
        <table cellspacing="0" cellpadding="0" :class="$style.rowTable">
          <colgroup>
            <col width="20%" />
            <col width="30%" />
            <col width="12%" />
            <col width="8%" />
            <col width="30%" />
          </colgroup>
          <tbody>
            <tr>
              <th scope="row"
                ><div>{{ $t('admin.c2c_market_seq') }}</div></th
              >
              <td>{{ params.seq || $t('admin.auto') }}</td>
              <th colspan="2" scope="row"
                ><div>{{ $t('admin.c2c_market_regdate') }}</div></th
              >
              <td>{{
                params.seq
                  ? date('Y-m-d H:i:s', new Date(params.regdate))
                  : $t('admin.before_enroll')
              }}</td>
            </tr>
            <tr>
              <th scope="row"
                ><div>{{ $t('admin.c2c_market_title') }}</div></th
              >
              <td
                ><LangInput v-if="!loading" v-model="params.title"></LangInput
              ></td>
              <th colspan="2" scope="row"
                ><div>{{ $t('admin.modal_is_active') }}</div></th
              >
              <td
                ><ElSwitch
                  v-model="params.is_active"
                  active-value="Y"
                  inactive-value="N"
                ></ElSwitch
              ></td>
            </tr>
            <tr>
              <th scope="row"
                ><div>{{ $t('admin.source_asset_title') }}</div></th
              >
              <td>
                <ElSelect
                  v-if="!params.seq"
                  v-model="params.source_asset_seq"
                  filterable
                  placeholder="Select"
                >
                  <ElOption
                    v-for="source_asset in assets"
                    :key="source_asset.seq"
                    :label="`${source_asset.title} (${source_asset.code})`"
                    :value="source_asset.seq"
                  >
                  </ElOption>
                </ElSelect>
                <template v-else>{{ params.source_asset_title }}</template>
              </td>
              <th colspan="2" scope="row"
                ><div>{{ $t('admin.target_asset_title') }}</div></th
              >
              <td>
                <ElSelect
                  v-if="!params.seq"
                  v-model="params.target_asset_seq"
                  filterable
                  placeholder="Select"
                >
                  <ElOption
                    v-for="target_asset in assets"
                    :key="target_asset.seq"
                    :label="`${target_asset.title} (${target_asset.code})`"
                    :value="target_asset.seq"
                  >
                  </ElOption>
                </ElSelect>
                <template v-else>{{ params.target_asset_title }}</template>
              </td>
            </tr>
            <tr>
              <th scope="row"
                ><div>{{ $t('admin.market_source_min_amount') }}</div></th
              >
              <td>
                <ElSwitch
                  v-model="params.use_source_min_amount"
                  :active-text="$t('admin.allow')"
                  active-value="Y"
                  inactive-value="N"
                  :inactive-text="$t('admin.not_allow')"
                ></ElSwitch>
                <input
                  v-if="params.use_source_min_amount === 'Y'"
                  v-model.number="params.source_min_amount"
                  type="text"
                  :class="[
                    $style.fr,
                    $style.inputTypeText,
                    $style.w80,
                    $style.tRight,
                  ]"
                />
              </td>
              <th colspan="2" scope="row"
                ><div>{{ $t('admin.market_source_max_amount') }}</div></th
              >
              <td>
                <ElSwitch
                  v-model="params.use_source_max_amount"
                  :active-text="$t('admin.allow')"
                  active-value="Y"
                  inactive-value="N"
                  :inactive-text="$t('admin.not_allow')"
                ></ElSwitch>
                <input
                  v-if="params.use_source_max_amount === 'Y'"
                  v-model.number="params.source_max_amount"
                  type="text"
                  :class="[
                    $style.fr,
                    $style.inputTypeText,
                    $style.w80,
                    $style.tRight,
                  ]"
                />
              </td>
            </tr>
            <tr>
              <th scope="row"
                ><div>{{
                  $t('private_market_info.direct_exchange_market')
                }}</div></th
              >
              <td colspan="3">
                <select v-model="params.use_follow_direct_market">
                  <option
                    v-for="val in [
                      ['N', $t('private_market_info.do_not_follow')],
                      ['Y', $t('private_market_info.follow')],
                    ]"
                    :key="val[0]"
                    :value="val[0]"
                    >{{ val[1] }}</option
                  >
                </select>
                <template v-if="params.use_follow_direct_market === 'Y'">
                  하고
                  <select v-model="params.follow_direct_market_seq">
                    <option :value="0">{{
                      $t('private_market_info.choice')
                    }}</option>
                    <option
                      v-for="direct_market in direct_markets"
                      :key="direct_market.seq"
                      :value="direct_market.seq"
                      >{{ direct_market.source_asset_code }}/{{
                        direct_market.target_asset_code
                      }}</option
                    >
                  </select>
                  {{ $t('private_market_info.designated') }}
                </template>
              </td>
            </tr>
            <tr v-if="params.use_follow_direct_market === 'Y'">
              <th scope="row"
                ><div>{{ $t('private_market_info.minimum_spread') }}</div></th
              >
              <td>
                <ElSwitch
                  v-model="params.use_min_spread_ratio"
                  :active-text="$t('admin.allow')"
                  active-value="Y"
                  inactive-value="N"
                  :inactive-text="$t('admin.not_allow')"
                ></ElSwitch>
                <input
                  v-if="params.use_min_spread_ratio === 'Y'"
                  v-model.number="params.min_spread_ratio"
                  type="text"
                  :class="[
                    $style.fr,
                    $style.inputTypeText,
                    $style.w80,
                    $style.tRight,
                  ]"
                />
              </td>
              <th colspan="2" scope="row"
                ><div>{{ $t('private_market_info.maximum_spread') }}</div></th
              >
              <td>
                <ElSwitch
                  v-model="params.use_max_spread_ratio"
                  :active-text="$t('admin.allow')"
                  active-value="Y"
                  inactive-value="N"
                  :inactive-text="$t('admin.not_allow')"
                ></ElSwitch>
                <input
                  v-if="params.use_max_spread_ratio === 'Y'"
                  v-model.number="params.max_spread_ratio"
                  type="text"
                  :class="[
                    $style.fr,
                    $style.inputTypeText,
                    $style.w80,
                    $style.tRight,
                  ]"
                />
              </td>
            </tr>
            <tr v-else>
              <th scope="row"
                ><div>{{ $t('admin.deal_min_price') }}</div></th
              >
              <td>
                <ElSwitch
                  v-model="params.use_min_price"
                  :active-text="$t('admin.allow')"
                  active-value="Y"
                  inactive-value="N"
                  :inactive-text="$t('admin.not_allow')"
                ></ElSwitch>
                <input
                  v-if="params.use_min_price === 'Y'"
                  v-model.number="params.min_price"
                  type="text"
                  :class="[
                    $style.fr,
                    $style.inputTypeText,
                    $style.w80,
                    $style.tRight,
                  ]"
                />
              </td>
              <th colspan="2" scope="row"
                ><div>{{ $t('admin.deal_max_price') }}</div></th
              >
              <td>
                <ElSwitch
                  v-model="params.use_max_price"
                  :active-text="$t('admin.allow')"
                  active-value="Y"
                  inactive-value="N"
                  :inactive-text="$t('admin.not_allow')"
                ></ElSwitch>
                <input
                  v-if="params.use_max_price === 'Y'"
                  v-model.number="params.max_price"
                  type="text"
                  :class="[
                    $style.fr,
                    $style.inputTypeText,
                    $style.w80,
                    $style.tRight,
                  ]"
                />
              </td>
            </tr>
            <tr>
              <th :class="[$style.tMiddle]" rowspan="2" scope="row"
                ><div>{{ $t('admin.c2c_source_amount_unit') }}</div></th
              >
              <td :class="[$style.tMiddle]" rowspan="2">
                <ElSwitch
                  v-model="params.use_source_amount_unit"
                  :active-text="$t('admin.allow')"
                  active-value="Y"
                  inactive-value="N"
                  :inactive-text="$t('admin.not_allow')"
                ></ElSwitch>
                <input
                  v-if="params.use_source_amount_unit === 'Y'"
                  v-model.number="params.source_amount_unit"
                  type="text"
                  :class="[
                    $style.fr,
                    $style.inputTypeText,
                    $style.w80,
                    $style.tRight,
                  ]"
                />
              </td>
              <th :class="[$style.tMiddle]" rowspan="2" scope="row"
                ><div>{{ $t('admin.market_fee_rate') }}</div></th
              >
              <th scope="row">{{ $t('private_market_info.announces ') }}</th>
              <td>
                <input
                  v-model.number="params.deal_fee_rate"
                  type="text"
                  :class="[$style.inputTypeText, $style.w80, $style.tRight]"
                />
                %
              </td>
            </tr>
            <tr>
              <th scope="row">{{ $t('private_market_info.solicitor ') }}</th>
              <td>
                <input
                  v-model.number="params.fee_rate"
                  type="text"
                  :class="[$style.inputTypeText, $style.w80, $style.tRight]"
                />
                %
              </td>
            </tr>
            <tr>
              <th scope="row">{{ $t('private_market_info.safety_type ') }}</th>
              <td colspan="4">
                <select v-model="params.escrow_type">
                  <option
                    v-for="val in constants.market.private_market.escrow_type"
                    :key="val[0]"
                    :value="val[0]"
                    >{{ val[1] }}</option
                  >
                </select>
                하도록 하여
                <template v-if="params.escrow_type === 'BOTH'">
                  {{ $t('private_market_info.msg_immediately') }}
                </template>
                <template v-if="params.escrow_type === 'DEALER'">
                  {{ $t('private_market_info.msg_disclosure') }}
                </template>
                <template v-if="params.escrow_type === 'APPLICANT'">
                  {{ $t('private_market_info.msg_requester') }}
                </template>
                <template v-if="params.escrow_type === 'DISABLE'">
                  {{ $t('private_market_info.msg_both_sides') }}
                </template>
              </td>
            </tr>
            <tr>
              <th colspan="5" :class="$style.button">
                <span :class="[$style.button, $style.black]"
                  ><input
                    type="submit"
                    :value="params.seq ? $t('admin.save') : $t('admin.enroll')"
                /></span>
              </th>
            </tr>
          </tbody>
        </table>
      </form>
    </el-tab-pane>
    <el-tab-pane
      :disabled="!params.seq"
      :label="$t('admin.deal_form')"
      name="deal_form"
    >
      <FormEditor
        v-if="params.seq"
        target_type="MARKET_PRIVATE_DEAL"
        :target_seq="params.seq"
      ></FormEditor>
    </el-tab-pane>
    <el-tab-pane
      :disabled="!params.seq"
      :label="$t('private_market_info.form')"
      name="application_form"
    >
      <FormEditor
        v-if="params.seq"
        target_type="MARKET_PRIVATE_APPLICATION"
        :target_seq="params.seq"
      ></FormEditor>
    </el-tab-pane>
  </el-tabs>
</template>

<style lang="scss" module>
@import '@src/design/default.scss';
@import '@src/design/admin.scss';
@import '@src/design/font.scss';
@import '@src/design/button.scss';
</style>
